import { useState, useRef, useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { apiService } from '@/service/api'
import type { ConnectorRecord, DiscoveryReport } from '@/types/connector'

type ScopePanelProps = {
  connector: ConnectorRecord
  // Emits the chosen AF scope as config-jsonb keys whenever a selection changes.
  // The owner (ScopeModal) persists it via updateConnector — ScopePanel is self-contained.
  onScopeChange: (scope: Record<string, unknown>) => void
  // Lets the owner (ScopeModal) disable Cancel/Save while a discovery is in flight.
  onDiscoveringChange?: (discovering: boolean) => void
}

const POLL_INTERVAL_MS = 3000
const MAX_POLL_ATTEMPTS = 30

// Harden a report from the backend (stored as Record<string,any>): default every array so
// a malformed/partial payload can't crash the picker on `.map`/`.length`.
const normalizeReport = (
  r: DiscoveryReport | null | undefined
): DiscoveryReport | null =>
  r
    ? {
        database: r.database ?? null,
        servers: r.servers ?? [],
        databases: r.databases ?? [],
        templates: r.templates ?? [],
        categories: r.categories ?? [],
        plant_candidates: r.plant_candidates ?? [],
        tag_count: r.tag_count ?? 0,
        capped: r.capped ?? false,
        sample_tags: r.sample_tags ?? [],
      }
    : null

const ScopePanel = ({ connector, onScopeChange, onDiscoveringChange }: ScopePanelProps) => {
  const { t } = useTranslation()

  // Seeded from any existing report already on the connector
  const [report, setReport] = useState<DiscoveryReport | null>(
    normalizeReport(connector.discovery_report)
  )
  const [discovering, setDiscovering] = useState(false)

  // Surface the in-flight state so the owning modal can disable Cancel/Save.
  useEffect(() => {
    onDiscoveringChange?.(discovering)
  }, [discovering, onDiscoveringChange])

  // Selections for scope output
  const [selectedDb, setSelectedDb] = useState<string>(
    (connector.config?.af_database as string | undefined) ?? ''
  )
  const [selectedPlants, setSelectedPlants] = useState<string[]>(
    (connector.config?.plant_elements as string[] | undefined) ?? []
  )
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>(
    (connector.config?.af_templates as string[] | undefined) ?? []
  )
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    (connector.config?.af_categories as string[] | undefined) ?? []
  )
  const [maxTags, setMaxTags] = useState<string>(
    (connector.config?.max_discovered_tags as number | undefined)?.toString() ?? ''
  )
  // discovery_interval is a duration STRING ("24h", "12h") — the connector parses it
  // with parse_duration, so it must NOT be coerced to a number.
  const [discoveryInterval, setDiscoveryInterval] = useState<string>(
    (connector.config?.discovery_interval as string | undefined) ?? ''
  )

  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const stopPolling = () => {
    if (pollTimerRef.current !== null) {
      clearInterval(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }

  // Stop the poll timer on unmount (e.g. the modal closes mid-discovery) so it doesn't
  // keep firing getConnector + setState on an unmounted component.
  useEffect(() => () => stopPolling(), [])

  const pushScopeToConfig = (
    db: string,
    plants: string[],
    templates: string[],
    categories: string[],
    cap: string,
    interval: string
  ) => {
    onScopeChange({
      af_database: db,
      plant_elements: plants,
      af_templates: templates,
      af_categories: categories,
      max_discovered_tags: cap !== '' ? Number(cap) : null,
      discovery_interval: interval !== '' ? interval : null,
    })
  }

  // Request a discovery pass for an (optional) database and poll until the report lands.
  // No database → the picklist pass (returns the database list); a database → that DB's
  // templates / categories / plant candidates. Selecting a DB calls this automatically.
  // scopeOverride lets toggleItem pass freshly-computed selections before React state
  // has flushed (setState is async; the closure would otherwise see stale state).
  const runDiscover = async (
    database?: string,
    scopeOverride?: { plants: string[]; templates: string[]; categories: string[] }
  ) => {
    stopPolling() // cancel any in-flight poll (e.g. rapid DB switches)
    setDiscovering(true)
    const requestedAt = new Date()

    const plants = scopeOverride ? scopeOverride.plants : selectedPlants
    const templates = scopeOverride ? scopeOverride.templates : selectedTemplates
    const categories = scopeOverride ? scopeOverride.categories : selectedCategories

    try {
      await apiService.requestConnectorDiscovery(connector.id, {
        database: database && database !== '' ? database : undefined,
        plant_elements: plants,
        af_templates: templates,
        af_categories: categories,
      })
    } catch {
      setDiscovering(false)
      toast.error(
        t('connectors:scope.discover_failed', {
          defaultValue: 'Discovery request failed',
        })
      )
      return
    }

    let attempts = 0
    pollTimerRef.current = setInterval(async () => {
      attempts++
      if (attempts > MAX_POLL_ATTEMPTS) {
        stopPolling()
        setDiscovering(false)
        return
      }

      try {
        const data = await apiService.getConnector(connector.id)
        const fetched = data.connector
        const reportAt = fetched.discovery_report_at
          ? new Date(fetched.discovery_report_at)
          : null

        if (reportAt && reportAt > requestedAt && fetched.discovery_report) {
          stopPolling()
          setDiscovering(false)
          setReport(normalizeReport(fetched.discovery_report))
          // If the report carries a database, seed the selector
          if (fetched.discovery_report.database) {
            setSelectedDb(fetched.discovery_report.database)
          }
        }
      } catch {
        // transient error — keep polling
      }
    }, POLL_INTERVAL_MS)
  }

  // Manual button: picklist pass if no DB chosen yet, else refresh the chosen DB.
  const handleDiscover = () => runDiscover(selectedDb !== '' ? selectedDb : undefined)

  const handleDbChange = (db: string) => {
    setSelectedDb(db)
    pushScopeToConfig(db, selectedPlants, selectedTemplates, selectedCategories, maxTags, discoveryInterval)
    // Auto-run discovery for the chosen DB so its templates/categories/plants load
    // without a second manual "Discover now" click.
    runDiscover(db)
  }

  const toggleItem = (
    item: string,
    current: string[],
    setter: (v: string[]) => void,
    key: 'plant_elements' | 'af_templates' | 'af_categories'
  ) => {
    const next = current.includes(item)
      ? current.filter((x) => x !== item)
      : [...current, item]
    setter(next)

    const newPlants = key === 'plant_elements' ? next : selectedPlants
    const newTemplates = key === 'af_templates' ? next : selectedTemplates
    const newCategories = key === 'af_categories' ? next : selectedCategories

    pushScopeToConfig(selectedDb, newPlants, newTemplates, newCategories, maxTags, discoveryInterval)

    // Re-run discovery with the narrowed scope so tag_count reflects the current selection.
    // Pass freshly-computed values as scopeOverride because React state hasn't flushed yet.
    runDiscover(selectedDb !== '' ? selectedDb : undefined, {
      plants: newPlants,
      templates: newTemplates,
      categories: newCategories,
    })
  }

  const handleCapChange = (value: string) => {
    setMaxTags(value)
    pushScopeToConfig(selectedDb, selectedPlants, selectedTemplates, selectedCategories, value, discoveryInterval)
  }

  const handleIntervalChange = (value: string) => {
    setDiscoveryInterval(value)
    pushScopeToConfig(selectedDb, selectedPlants, selectedTemplates, selectedCategories, maxTags, value)
  }

  return (
    <div className="grid gap-4 border rounded-md p-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold">{t('connectors:scope.title')}</span>
        <Button
          size="sm"
          variant="outline"
          onClick={handleDiscover}
          disabled={discovering}
        >
          {discovering ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t('connectors:scope.discovering')}
            </>
          ) : (
            t('connectors:scope.discover')
          )}
        </Button>
      </div>

      {discovering && (
        <p className="text-xs text-muted-foreground">{t('connectors:scope.discovering')}&hellip;</p>
      )}

      {report && (
        <div className="grid gap-3">
          {/* AF Database selector */}
          <div className="grid gap-1">
            <Label className="text-xs">{t('connectors:scope.af_database')}</Label>
            <Select value={selectedDb} onValueChange={handleDbChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('connectors:scope.af_database')} />
              </SelectTrigger>
              <SelectContent>
                {report.databases.map((db) => (
                  <SelectItem key={db} value={db}>
                    {db}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Plant candidates */}
          {report.plant_candidates.length > 0 && (
            <div className="grid gap-1">
              <Label className="text-xs">{t('connectors:scope.plants')}</Label>
              <div className="flex flex-wrap gap-2">
                {report.plant_candidates.map((plant) => (
                  <div key={plant} className="flex items-center gap-1">
                    <Checkbox
                      id={`plant-${plant}`}
                      checked={selectedPlants.includes(plant)}
                      onCheckedChange={() =>
                        toggleItem(plant, selectedPlants, setSelectedPlants, 'plant_elements')
                      }
                    />
                    <label htmlFor={`plant-${plant}`} className="text-xs cursor-pointer">
                      {plant}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Templates */}
          {report.templates.length > 0 && (
            <div className="grid gap-1">
              <Label className="text-xs">{t('connectors:scope.templates')}</Label>
              <div className="flex flex-wrap gap-2">
                {report.templates.map((tmpl) => (
                  <div key={tmpl} className="flex items-center gap-1">
                    <Checkbox
                      id={`template-${tmpl}`}
                      checked={selectedTemplates.includes(tmpl)}
                      onCheckedChange={() =>
                        toggleItem(tmpl, selectedTemplates, setSelectedTemplates, 'af_templates')
                      }
                    />
                    <label htmlFor={`template-${tmpl}`} className="text-xs cursor-pointer">
                      {tmpl}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Categories */}
          {report.categories.length > 0 && (
            <div className="grid gap-1">
              <Label className="text-xs">{t('connectors:scope.categories')}</Label>
              <div className="flex flex-wrap gap-2">
                {report.categories.map((cat) => (
                  <div key={cat} className="flex items-center gap-1">
                    <Checkbox
                      id={`category-${cat}`}
                      checked={selectedCategories.includes(cat)}
                      onCheckedChange={() =>
                        toggleItem(cat, selectedCategories, setSelectedCategories, 'af_categories')
                      }
                    />
                    <label htmlFor={`category-${cat}`} className="text-xs cursor-pointer">
                      {cat}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tag count + a small sample of names (preview, not the full list) + cap warning */}
          <div className="grid gap-1 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <span>{t('connectors:scope.tag_count')}: {report.tag_count}</span>
              {report.capped && (
                <span className="text-yellow-600">{t('connectors:scope.capped_warning')}</span>
              )}
            </div>
            {report.sample_tags && report.sample_tags.length > 0 && (
              <span className="font-mono truncate">
                {report.sample_tags.join(', ')}
                {report.tag_count > report.sample_tags.length
                  ? t('connectors:scope.sample_more', {
                      count: report.tag_count - report.sample_tags.length,
                      defaultValue: ' …and {{count}} more',
                    })
                  : ''}
              </span>
            )}
          </div>

          {/* Cap input */}
          <div className="grid gap-1">
            <Label className="text-xs" htmlFor="scope-cap">{t('connectors:scope.cap')}</Label>
            <Input
              id="scope-cap"
              type="number"
              value={maxTags}
              onChange={(e) => handleCapChange(e.target.value)}
              className="w-40"
            />
          </div>

          {/* Discovery interval */}
          <div className="grid gap-1">
            <Label className="text-xs" htmlFor="scope-interval">{t('connectors:scope.interval')}</Label>
            <Input
              id="scope-interval"
              type="text"
              placeholder="24h"
              value={discoveryInterval}
              onChange={(e) => handleIntervalChange(e.target.value)}
              className="w-40"
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default ScopePanel
